import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Bell, Mail, ExternalLink, Copy, Check, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Telegram icon component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export default function NotificationSettingsPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [linkCode, setLinkCode] = useState(null);
  const [botInfo, setBotInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  // Check team_roles for team management positions
  const teamManagementRoles = ["captain", "team_captain", "vice_captain", "manager"];
  const hasTeamManagementRole = (user?.team_roles || []).some(tr => teamManagementRoles.includes(tr.role));
  const isCaptain = ["convenor", "captain", "team_captain"].includes(user?.role) || hasTeamManagementRole;

  useEffect(() => {
    fetchSettings();
    fetchBotInfo();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`, {
        withCredentials: true
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchBotInfo = async () => {
    try {
      const response = await axios.get(`${API}/telegram/bot-info`);
      setBotInfo(response.data);
    } catch (error) {
      console.error("Error fetching bot info:", error);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/settings`, newSettings, {
        withCredentials: true
      });
      setSettings(prev => ({ ...prev, ...newSettings }));
      toast.success("Settings saved");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (channel, enabled) => {
    saveSettings({ [`${channel}_enabled`]: enabled });
  };

  const togglePreference = (pref, enabled) => {
    const newPrefs = { ...settings.preferences, [pref]: enabled };
    saveSettings({ preferences: newPrefs });
  };

  const generateLinkCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await axios.get(`${API}/telegram/link-code`, {
        withCredentials: true
      });
      setLinkCode(response.data);
    } catch (error) {
      console.error("Error generating link code:", error);
      toast.error("Failed to generate link code");
    } finally {
      setGeneratingCode(false);
    }
  };

  const unlinkTelegram = async () => {
    if (!window.confirm("Unlink your Telegram account? You will stop receiving notifications on Telegram.")) {
      return;
    }
    
    try {
      await axios.post(`${API}/telegram/unlink`, {}, { withCredentials: true });
      setSettings(prev => ({
        ...prev,
        telegram_enabled: false,
        telegram_chat_id: null,
        telegram_linked: false
      }));
      setLinkCode(null);
      toast.success("Telegram account unlinked");
    } catch (error) {
      console.error("Error unlinking Telegram:", error);
      toast.error("Failed to unlink Telegram");
    }
  };

  const copyCode = () => {
    if (linkCode?.code) {
      navigator.clipboard.writeText(`/link ${linkCode.code}`);
      setCopied(true);
      toast.success("Code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const updateEmail = (email) => {
    saveSettings({ notification_email: email });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const isAcademyParent = user?.roles?.includes("academy_parent");

  const notificationTypes = [
    { key: "game_scheduled", label: "New Game Scheduled", desc: "When a new game is created for your team", forAll: true },
    { key: "game_signup", label: "Player Signups", desc: "When someone signs up for your game", forCaptain: true },
    { key: "game_withdrawal", label: "Player Withdrawals", desc: "When someone withdraws from your game", forCaptain: true },
    { key: "game_decline", label: "Player Unavailable", desc: "When someone marks themselves unavailable", forCaptain: true },
    { key: "game_cancelled", label: "Game Cancelled", desc: "When a game is cancelled", forAll: true },
    { key: "standby_promoted", label: "Standby Promotion", desc: "When you're promoted from standby to main squad", forAll: true },
    { key: "match_report", label: "Match Reports", desc: "When a match report is published", forAll: true },
    { key: "event_scheduled", label: "Events", desc: "When new events are scheduled", forAll: true },
    { key: "signup_approved", label: "Signup Approved", desc: "When captain approves your signup", forAll: true },
    { key: "signup_rejected", label: "Signup Rejected", desc: "When captain rejects your signup", forAll: true },
    { key: "academy_session_scheduled", label: "Academy Sessions", desc: "When new training sessions or games are scheduled for your child's team", forAcademyParent: true },
  ];

  const filteredTypes = notificationTypes.filter(t => 
    t.forAll || 
    (t.forCaptain && isCaptain) ||
    (t.forAcademyParent && isAcademyParent)
  );

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5 px-2">
              <ArrowLeft className="w-5 h-5 md:mr-2" />
              <span className="hidden md:inline">Back</span>
            </Button>
          </Link>
          <h1 className="text-lg md:text-xl font-bold text-yellow-500 font-display">Notification Settings</h1>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Notification Channels */}
        <Card className="p-6 mb-6 bg-slate-900/50 border border-white/5 rounded-xl">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-500" />
            Notification Channels
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Choose how you want to receive notifications
          </p>

          {/* Email Channel */}
          <div className="bg-slate-800/50 border border-white/5 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Email Notifications</h3>
                  <p className="text-sm text-slate-400">Receive notifications via email</p>
                </div>
              </div>
              <Switch
                checked={settings?.email_enabled ?? true}
                onCheckedChange={(checked) => toggleChannel("email", checked)}
              />
            </div>
            
            {settings?.email_enabled && (
              <div className="mt-3 pt-3 border-t border-white/5">
                <label className="text-sm font-medium text-slate-300 block mb-2">
                  Notification Email (leave empty to use account email)
                </label>
                <Input
                  type="email"
                  placeholder={user?.email || "your@email.com"}
                  defaultValue={settings?.notification_email || ""}
                  onBlur={(e) => updateEmail(e.target.value)}
                  className="max-w-sm bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-yellow-500/50"
                />
              </div>
            )}
          </div>

          {/* Telegram Channel */}
          <div className="bg-slate-800/50 border border-white/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0088cc]/20 rounded-lg flex items-center justify-center">
                  <TelegramIcon className="w-5 h-5 text-[#0088cc]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Telegram Notifications</h3>
                  <p className="text-sm text-slate-400">Instant notifications on your phone</p>
                </div>
              </div>
              <Switch
                checked={settings?.telegram_enabled ?? false}
                onCheckedChange={(checked) => toggleChannel("telegram", checked)}
                disabled={!settings?.telegram_linked}
              />
            </div>

            {/* Telegram Link Status */}
            {settings?.telegram_linked ? (
              <div className="mt-3 pt-3 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">Connected to Telegram</span>
                    {settings?.telegram_username && (
                      <span className="text-slate-400">(@{settings.telegram_username})</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={unlinkTelegram}
                    className="text-red-400 border-red-500/30 hover:bg-red-500/10 hover:border-red-500/50"
                  >
                    Unlink
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-3 pt-3 border-t border-white/5">
                {!linkCode ? (
                  <div className="text-center py-2">
                    <p className="text-sm text-slate-400 mb-3">
                      Link your Telegram to receive instant notifications
                    </p>
                    <Button
                      onClick={generateLinkCode}
                      disabled={generatingCode}
                      className="bg-[#0088cc] hover:bg-[#0077b5] text-white"
                    >
                      {generatingCode ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <TelegramIcon className="w-4 h-4 mr-2" />
                      )}
                      Link Telegram
                    </Button>
                  </div>
                ) : (
                  <div className="bg-[#0088cc]/10 rounded-lg p-4 border border-[#0088cc]/20">
                    <h4 className="font-semibold text-white mb-3">Link Your Telegram</h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="bg-[#0088cc] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                        <span className="text-sm text-slate-300">
                          Open Telegram and search for{" "}
                          {botInfo?.username ? (
                            <a 
                              href={botInfo.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#0088cc] font-semibold hover:underline"
                            >
                              @{botInfo.username}
                              <ExternalLink className="w-3 h-3 inline ml-1" />
                            </a>
                          ) : (
                            "the bot"
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="bg-[#0088cc] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                        <span className="text-sm text-slate-300">Send this command to the bot:</span>
                      </div>
                      
                      <div className="flex items-center gap-2 bg-slate-800/80 rounded-lg border border-white/10 p-3">
                        <code className="flex-1 font-mono text-lg font-bold text-[#0088cc]">
                          /link {linkCode.code}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyCode}
                          className="shrink-0 border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      
                      <p className="text-xs text-slate-400">
                        Code expires in {linkCode.expires_in_minutes} minutes
                      </p>
                      
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={generateLinkCode}
                          className="text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          New Code
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={fetchSettings}
                          className="text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          I&apos;ve Linked
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Notification Preferences */}
        <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
          <h2 className="text-xl font-bold text-white mb-2">What to Notify Me About</h2>
          <p className="text-sm text-slate-400 mb-6">
            Choose which events you want to be notified about
          </p>

          <div className="space-y-1">
            {filteredTypes.map((type) => (
              <div key={type.key} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                <div>
                  <h4 className="font-medium text-white">{type.label}</h4>
                  <p className="text-sm text-slate-400">{type.desc}</p>
                  {type.forCaptain && !type.forAll && (
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded mt-1 inline-block">
                      Captain Only
                    </span>
                  )}
                  {type.forAcademyParent && !type.forAll && (
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded mt-1 inline-block">
                      Academy Parent
                    </span>
                  )}
                </div>
                <Switch
                  checked={settings?.preferences?.[type.key] ?? true}
                  onCheckedChange={(checked) => togglePreference(type.key, checked)}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Info Note */}
        <p className="text-center text-sm text-slate-400 mt-6">
          You can also manage notifications from your profile settings
        </p>
      </div>
    </div>
  );
}
